import React, { useRef } from 'react';
import { Prompt, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useFormikContext } from 'formik';
import type { ISchema } from 'yup';
import { showToast } from '@klpkonsern/ui';

import { useDebouncedEffect, validateValuesSync } from '../utils';
import {
    updateProsjektProcessFormfieldsCache,
    useSaveProsjektProcessFormfieldsMutation,
} from '../services/rtkApi';
import type { FormField } from '../interfaces';
import type { FormValuesType } from './FormContainer';

type AutoSaveFormfieldsProps = {
    activityId: string | null;
    fields: Array<FormField>;
    isDisabled: boolean;
    // Schema without required rules: blocks saving invalid values, not drafts
    validationSchema: ISchema<unknown>;
};

export const AutoSaveFormfields = ({
    activityId,
    fields,
    isDisabled,
    validationSchema,
}: AutoSaveFormfieldsProps) => {
    const { t, i18n } = useTranslation();
    const { resetForm, values, dirty, errors, touched } =
        useFormikContext<FormValuesType>();
    const dispatch = useDispatch();
    const { prosessInstansId } = useParams<{
        prosessInstansId: string;
    }>();
    const [saveForm, { isLoading }] =
        useSaveProsjektProcessFormfieldsMutation();

    const isDisabledRef = useRef(isDisabled);
    const errorsRef = useRef(errors);
    const touchedRef = useRef(touched);
    errorsRef.current = errors;
    touchedRef.current = touched;

    const handleSaveForm = async () => {
        if (!activityId || isDisabledRef.current) return;

        // Gate on the exact values being saved. Formik's errors state only
        // updates on submit (validateOnChange is off) and lags behind values,
        // so it can't tell whether this snapshot is safe to save.
        if (
            Object.keys(validateValuesSync(values, validationSchema)).length > 0
        ) {
            return;
        }

        const updatedFormFields = fields.map((field) => ({
            ...field,
            value: values?.[field?.id as string],
        }));
        const payload = {
            oppgaveId: activityId,
            formFields: updatedFormFields,
        };

        resetForm({
            values,
            errors: errorsRef.current,
            touched: touchedRef.current,
        });
        await saveForm(payload)
            .then(() =>
                dispatch(
                    updateProsjektProcessFormfieldsCache(
                        prosessInstansId,
                        i18n.language,
                        updatedFormFields,
                        activityId,
                    ),
                ),
            )
            .catch(() => {
                showToast({
                    body: t('project.errors.savePrompt'),
                    type: 'error',
                });
            });
    };

    useDebouncedEffect(
        handleSaveForm,
        [values],
        1500,
        isDisabled || isLoading || !dirty,
    );

    return (
        <Prompt
            when={dirty && !isDisabled}
            message={() => {
                handleSaveForm();
                return true;
            }}
        />
    );
};
